// src/components/MenuManagement.jsx
//
// Pattern: ตาม USAShipmentDatagrid
//   - Container / Grid จาก @material-ui/core
//   - Button1 จาก @material-ui/core/Button
//   - useStyles + className="head" / "searchButton"
//   - Toast แบบ fixed top-right
//   - Modal inline style เหมือนกัน
//   - api_path จาก shared.js
//
import React, { useEffect, useState, useCallback } from "react";
import Button1       from "@material-ui/core/Button";
import Grid          from "@material-ui/core/Grid";
import Container     from "@material-ui/core/Container";

import { useStyles } from "../../Styles.js";   // ← same as USAShipmentDatagrid
import { useMenus }  from "../../hooks/useMenus";
import { MenuTree }  from "./MenuTree";
import { MenuDetail } from "./MenuDetail";
import { MenuForm }   from "./MenuForm";
import { ConfirmDialog } from "./ConfirmDialog";
import { Toast }         from "./Toast";

// ─────────────────────────────────────────────────────────────────────────────
export function MenuManagement() {
  const classes = useStyles();
  const {
    menus, loading, error,
    clearError, fetchAll,
    create, update, remove,
  } = useMenus();

  const [selectedId,  setSelectedId]  = useState(null);
  const [formOpen,    setFormOpen]    = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);   // null = add mode
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast,       setToast]       = useState(null);

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const selectedMenu = menus.find(m => m.MENU_ID === selectedId) ?? null;

  // ── Open add form ─────────────────────────────────────────────────────────
  const handleOpenAdd = useCallback((parentId) => {
    setEditingMenu(parentId
      ? {
          MENU_PARENT_ID: parentId,
          MENU_LEVEL: (menus.find(m => m.MENU_ID === parentId)?.MENU_LEVEL ?? 1) + 1,
        }
      : null
    );
    setFormOpen(true);
  }, [menus]);

  // ── Open edit form ────────────────────────────────────────────────────────
  const handleOpenEdit = useCallback(() => {
    setEditingMenu(selectedMenu);
    setFormOpen(true);
  }, [selectedMenu]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (payload) => {
    try {
      if (editingMenu?.MENU_ID) {
        const updated = await update(editingMenu.MENU_ID, payload);
        setSelectedId(updated.MENU_ID);
        showToast(`✅ แก้ไข "${updated.MENU_TEXT}" สำเร็จ`);
      } else {
        const created = await create(payload);
        setSelectedId(created.MENU_ID);
        showToast(`✅ เพิ่ม "${created.MENU_TEXT}" สำเร็จ`);
      }
      setFormOpen(false);
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    try {
      await remove(selectedId);
      setSelectedId(null);
      setConfirmOpen(false);
      showToast("🗑️ ลบ menu สำเร็จ");
    } catch (e) {
      showToast("❌ " + e.message, "error");
    }
  };

  const childCount = menus.filter(m => m.MENU_PARENT_ID === selectedId).length;
  const deleteMsg  = childCount > 0
    ? `"${selectedMenu?.MENU_TEXT}" มี ${childCount} sub-menu ลบแล้วจะหายทั้งหมด ยืนยัน?`
    : `ยืนยันลบ "${selectedMenu?.MENU_TEXT}"? ไม่สามารถกู้คืนได้`;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl">
      <Toast toast={toast} />

      {/* Modals */}
      <MenuForm
        open={formOpen}
        menu={editingMenu?.MENU_ID ? editingMenu : null}
        allMenus={menus}
        onSave={handleSave}
        onClose={() => setFormOpen(false)}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="🗑️ ลบ Menu"
        message={deleteMsg}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* Spacer */}
      <Grid container spacing={2}>
        <Grid item xs={12}>&nbsp;</Grid>
      </Grid>

      {/* Page title */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10}>
          <label className="head">Menu Management</label>
        </Grid>
      </Grid>

      {/* Action buttons */}
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <Button1
            className="searchButton"
            variant="contained"
            color="primary"
            onClick={fetchAll}
            disabled={loading}
          >
            {loading ? "⏳ Loading..." : "Refresh"}
          </Button1>

          <Button1
            className="searchButton"
            variant="outlined"
            color="primary"
            onClick={() => handleOpenAdd(null)}
          >
            ➕ Add Menu
          </Button1>

          {/* Error banner */}
          {error && (
            <span style={{
              background:   "#fef2f2",
              color:        "#dc2626",
              border:       "1px solid #fca5a5",
              borderRadius: 6,
              padding:      "4px 12px",
              fontSize:     12,
              fontWeight:   600,
              cursor:       "pointer",
            }} onClick={clearError}>
              ❌ {error} (คลิกเพื่อปิด)
            </span>
          )}
        </Grid>
      </Grid>

      {/* Main layout: sidebar + detail */}
      <Grid container className={classes.root} style={{ marginTop: 12 }}>
        <div style={{
          display:      "flex",
          width:        "100%",
          border:       "1px solid #e2e8f0",
          borderRadius: 8,
          overflow:     "hidden",
          minHeight:    580,
          background:   "#fff",
        }}>
          {/* Left: Tree */}
          <MenuTree
            menus={menus}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAdd={handleOpenAdd}
          />

          {/* Right: Detail */}
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <MenuDetail
              menu={selectedMenu}
              allMenus={menus}
              onEdit={handleOpenEdit}
              onDelete={() => setConfirmOpen(true)}
            />
          </div>
        </div>
      </Grid>
    </Container>
  );
}
